import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Zap, Trash2, Play } from "lucide-react";

const DEFAULT_RULE = {
    name: "", trigger_event: "lead.created",
    conditions: [], actions: [{ type: "add_tag", tag: "" }],
    enabled: true,
};

function ConditionsEditor({ value, onChange, ops }) {
    const add = () => onChange([...(value || []), { field: "payload.source", op: "eq", value: "" }]);
    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    const patch = (i, k, v) => onChange(value.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
    return (
        <div className="space-y-2">
            {(value || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`rule-cond-${i}`}>
                    <Input className="w-52 h-8 font-mono text-xs" placeholder="payload.field.path"
                        value={c.field || ""} onChange={(e) => patch(i, "field", e.target.value)} />
                    <Select value={c.op || "eq"} onValueChange={(v) => patch(i, "op", v)}>
                        <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{(ops || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="w-48 h-8 text-xs" placeholder="value"
                        value={c.value ?? ""} onChange={(e) => patch(i, "value", e.target.value)} />
                    <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
            ))}
            <Button size="sm" variant="outline" onClick={add} data-testid="rule-cond-add">
                <Plus className="w-3.5 h-3.5 mr-1" />Add condition
            </Button>
        </div>
    );
}

function ActionsEditor({ value, onChange, actionTypes, tags = [] }) {
    const add = () => onChange([...(value || []), { type: "add_tag", tag_id: "" }]);
    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    const patch = (i, k, v) => onChange(value.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
    return (
        <div className="space-y-2">
            {(value || []).map((a, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 border-l-2 border-teal-500 pl-3 py-1" data-testid={`rule-action-${i}`}>
                    <Select value={a.type} onValueChange={(v) => patch(i, "type", v)}>
                        <SelectTrigger className="w-40 h-8" data-testid={`rule-action-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{(actionTypes || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                    {a.type === "add_tag" && (
                        <Select value={a.tag_id || ""}
                            onValueChange={(v) => {
                                const t = tags.find((x) => x.id === v);
                                patch(i, "tag_id", v);
                                // Also stash the name for readability in rule cards
                                if (t) patch(i, "tag", t.name);
                            }}>
                            <SelectTrigger className="w-56 h-8" data-testid={`rule-action-tag-${i}`}>
                                <SelectValue placeholder="Pick tag from catalogue" />
                            </SelectTrigger>
                            <SelectContent>
                                {tags.length === 0 && <div className="text-[11px] text-[#17212B]/50 px-2 py-1">Create tags in CRM → Tools first</div>}
                                {tags.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: t.color }} />
                                        {t.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {a.type === "send_touchpoint" && (
                        <Input className="w-56 h-8 text-xs" placeholder="Touchpoint key (e.g. welcome_signup)"
                            value={a.touchpoint || ""} onChange={(e) => patch(i, "touchpoint", e.target.value)} />
                    )}
                    {a.type === "create_task" && (<>
                        <Input className="w-52 h-8 text-xs" placeholder="Task title"
                            value={a.title || ""} onChange={(e) => patch(i, "title", e.target.value)} />
                        <Input className="w-24 h-8 text-xs" type="number" placeholder="Due (h)"
                            value={a.due_hours || 24} onChange={(e) => patch(i, "due_hours", parseInt(e.target.value || "24", 10))} />
                        <Select value={a.priority || "med"} onValueChange={(v) => patch(i, "priority", v)}>
                            <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>{["low", "med", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                    </>)}
                    {a.type === "move_stage" && (
                        <Input className="w-40 h-8 text-xs" placeholder="Stage key"
                            value={a.stage || ""} onChange={(e) => patch(i, "stage", e.target.value)} />
                    )}
                    {a.type === "notify_admin" && (
                        <Input className="flex-1 h-8 text-xs" placeholder="Message to admins"
                            value={a.message || ""} onChange={(e) => patch(i, "message", e.target.value)} />
                    )}
                    <Button variant="ghost" size="sm" onClick={() => remove(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
            ))}
            <Button size="sm" variant="outline" onClick={add} data-testid="rule-action-add">
                <Plus className="w-3.5 h-3.5 mr-1" />Add action
            </Button>
        </div>
    );
}

function RuleForm({ rule, onSave, onCancel, catalogue, tags }) {
    const [r, setR] = useState(rule || DEFAULT_RULE);
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Rule name" value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })}
                    data-testid="rule-name" />
                <Select value={r.trigger_event} onValueChange={(v) => setR({ ...r, trigger_event: v })}>
                    <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{(catalogue.events || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
            </div>
            <div>
                <div className="text-xs font-semibold text-[#123B5D] mb-1">Conditions (all AND'd; empty = fires on every event)</div>
                <ConditionsEditor value={r.conditions} onChange={(v) => setR({ ...r, conditions: v })} ops={catalogue.condition_ops} />
            </div>
            <div>
                <div className="text-xs font-semibold text-[#123B5D] mb-1">Actions</div>
                <ActionsEditor value={r.actions} onChange={(v) => setR({ ...r, actions: v })} actionTypes={catalogue.action_types} tags={tags} />
            </div>
            <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!r.enabled} onChange={(e) => setR({ ...r, enabled: e.target.checked })}
                    data-testid="rule-enabled" />
                Enabled
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button onClick={() => onSave(r)} disabled={!r.name || !r.actions.length} data-testid="rule-save">Save rule</Button>
            </div>
        </div>
    );
}

export default function SuperAdminAutomation() {
    const [rules, setRules] = useState([]);
    const [logs, setLogs] = useState([]);
    const [catalogue, setCatalogue] = useState({ events: [], action_types: [], condition_ops: [] });
    const [editing, setEditing] = useState(null); // null | rule
    const [testEvent, setTestEvent] = useState("");
    const [testPayload, setTestPayload] = useState("{}");
    const [tags, setTags] = useState([]);

    const refresh = async () => {
        try {
            const [c, r, l, tg] = await Promise.all([
                api.get(`/automation/catalogue`),
                api.get(`/automation/rules`),
                api.get(`/automation/logs?page_size=25`),
                api.get(`/crm/tags`),
            ]);
            setCatalogue(c.data);
            setRules(r.data.items || []);
            setLogs(l.data.items || []);
            setTags(tg.data.items || []);
            if (!testEvent && (c.data.events || []).length) setTestEvent(c.data.events[0]);
        } catch { toast.error("Failed to load"); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const save = async (r) => {
        try {
            if (r.id) await api.put(`/automation/rules/${r.id}`, r);
            else await api.post(`/automation/rules`, r);
            toast.success("Rule saved"); setEditing(null); refresh();
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete rule?")) return;
        await api.delete(`/automation/rules/${id}`); refresh();
    };
    const toggle = async (r) => {
        await api.post(`/automation/rules/${r.id}/toggle`, { enabled: !r.enabled }); refresh();
    };
    const emit = async () => {
        let payload = {};
        try { payload = JSON.parse(testPayload || "{}"); } catch { toast.error("Payload must be valid JSON"); return; }
        try {
            const r = await api.post(`/automation/emit`, { name: testEvent, payload });
            toast.success(`Event emitted: ${r.data.event_id}`);
            setTimeout(refresh, 1500);
        } catch { toast.error("Emit failed"); }
    };

    return (
        <AdminShell>
            <div className="mb-4 flex justify-between items-start">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin · Automation</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">Rules & Event Bus</h1>
                    <p className="text-[#17212B]/60 mt-1 text-sm">Declarative rules react to events across the platform. Actions are fire-and-forget and never block the emitting flow.</p>
                </div>
                <Button onClick={() => setEditing(DEFAULT_RULE)} data-testid="rule-new">
                    <Plus className="w-4 h-4 mr-1" />New rule
                </Button>
            </div>

            {/* Manual emit — dev/test */}
            <Card className="mb-4"><CardContent className="pt-4">
                <h3 className="text-sm font-bold text-[#123B5D] mb-2 flex items-center gap-2"><Play className="w-4 h-4" />Manual emit (test)</h3>
                <div className="flex flex-wrap items-end gap-2">
                    <Select value={testEvent} onValueChange={setTestEvent}>
                        <SelectTrigger className="w-56 h-9" data-testid="emit-event"><SelectValue /></SelectTrigger>
                        <SelectContent>{(catalogue.events || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                    <Textarea rows={2} placeholder='{"crm_record_id":"...","source":"signup"}' value={testPayload}
                        onChange={(e) => setTestPayload(e.target.value)}
                        className="flex-1 min-w-[280px] font-mono text-[11px]" data-testid="emit-payload" />
                    <Button onClick={emit} data-testid="emit-btn">Emit</Button>
                </div>
            </CardContent></Card>

            {/* Rules list */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                {rules.map((r) => (
                    <Card key={r.id} data-testid={`rule-${r.id}`}>
                        <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-bold text-[#123B5D]">{r.name}</h4>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${r.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                            {r.enabled ? "ON" : "OFF"}
                                        </span>
                                    </div>
                                    <div className="text-[11px] font-mono text-[#17212B]/60 mt-0.5">{r.trigger_event}</div>
                                    <div className="text-[11px] text-[#17212B]/70 mt-1">
                                        {(r.conditions || []).length} condition(s) · {(r.actions || []).length} action(s)
                                    </div>
                                    <div className="text-[11px] mt-1">
                                        {(r.actions || []).map((a, i) => (
                                            <span key={i} className="inline-block bg-teal-50 text-teal-700 rounded px-1.5 py-0.5 mr-1 mt-1">
                                                {a.type}{a.tag ? `:${a.tag}` : ""}{a.touchpoint ? `:${a.touchpoint}` : ""}{a.stage ? `→${a.stage}` : ""}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`rule-edit-${r.id}`}>Edit</Button>
                                    <Button size="sm" variant="ghost" onClick={() => toggle(r)} data-testid={`rule-toggle-${r.id}`}>{r.enabled ? "Disable" : "Enable"}</Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`rule-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {!rules.length && (
                    <Card><CardContent className="pt-4">
                        <div className="text-center py-6 text-[#17212B]/60 text-sm">
                            No automation rules yet. Create one to auto-tag hot signups, create follow-up tasks on failed payments, or dispatch touchpoints on stage changes.
                        </div>
                    </CardContent></Card>
                )}
            </div>

            {/* Logs */}
            <Card><CardContent className="pt-4">
                <h3 className="text-sm font-bold text-[#123B5D] mb-3 flex items-center gap-2"><Zap className="w-4 h-4" />Recent fires (25)</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs" data-testid="rules-logs-table">
                        <thead className="bg-[#F5F7FB] text-[#17212B]/60 uppercase text-[10px]">
                            <tr>
                                <th className="px-2 py-1 text-left">When</th>
                                <th className="px-2 py-1 text-left">Event</th>
                                <th className="px-2 py-1 text-left">Matched</th>
                                <th className="px-2 py-1 text-left">Actions</th>
                                <th className="px-2 py-1 text-left">Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((l) => (
                                <tr key={l.id} className="border-b">
                                    <td className="px-2 py-1 text-[#17212B]/60">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-2 py-1 font-mono">{l.event_name}</td>
                                    <td className="px-2 py-1">{l.matched ? "✓" : "—"}</td>
                                    <td className="px-2 py-1">{(l.actions_executed || []).map((a) => a.type).join(", ")}</td>
                                    <td className="px-2 py-1 text-red-600">{l.error || ""}</td>
                                </tr>
                            ))}
                            {!logs.length && (
                                <tr><td colSpan={5} className="text-center py-4 text-[#17212B]/50">No fires logged yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </CardContent></Card>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader><DialogTitle>{editing?.id ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
                    {editing && <RuleForm rule={editing} catalogue={catalogue} tags={tags} onSave={save} onCancel={() => setEditing(null)} />}
                </DialogContent>
            </Dialog>
        </AdminShell>
    );
}
